import { AppstoreOutlined, BarsOutlined, DownloadOutlined } from "@ant-design/icons"
import { Button, Col, Empty, Flex, Radio, Row, Skeleton } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { AssignDocumentTypeModal } from "../../Files/components/AssignDocumentTypeModal.tsx"
import { ShareDialog } from "../../Files/components/ShareDialog.tsx"
import { useFilesView } from "../../Files/helpers/files-view-preference.ts"
import { useFavorites } from "../../Files/hooks/useFavorites.ts"
import { FileCard } from "../../Files/components/FileCard.tsx"
import { type FileOnlyColumn, FilesListView } from "../../Files/components/FilesListView.tsx"
import { FileViewer } from "../../Files/components/FileViewer.tsx"
import { type FileItem } from "../../Files/types/file-item.types.ts"
import { RenameFileModal } from "../../Project/ProjectViewPage/components/RenameFileModal.tsx"
import { downloadFilesAsZip } from "../../../utils/download-files-zip.ts"
import { EMPTY_GLOBAL_FILES_FILTERS, useGlobalFilesData } from "../hooks/useGlobalFilesData.ts"
import { useGlobalFileActions } from "../hooks/useGlobalFileActions.ts"

interface GlobalFilesPhotosTabProps {
  search: string
}

// Photos are `Attachment` rows — narrower than a Document: no version
// chain, no review workflow, no comments/activity, no related-record link
// (`useGlobalFileActions.fileActions` already branches on `file.source` and
// only offers those for `"document"`, so this tab automatically gets the
// right, narrower set without a second action-list to maintain).
export function GlobalFilesPhotosTab({ search }: GlobalFilesPhotosTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [view, setView] = useFilesView()
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)

  const filters = useMemo(() => ({ ...EMPTY_GLOBAL_FILES_FILTERS, q: search }), [search])
  const { items, loading, failed, hasMore, loadingMore, loadMore, reload } = useGlobalFilesData(
    "photos",
    filters,
    "modified_desc"
  )

  const favorites = useFavorites("global")

  const navigateToProject = (file: FileItem) => {
    const info = file.projectInfo
    if (!info) return
    navigate(info.is_project ? RoutesPaths.project.view(info.id).overview : RoutesPaths.lead.view(info.id))
  }

  const actions = useGlobalFileActions({
    fileItems: items,
    reload,
    onViewProject: navigateToProject
  })

  const extraColumns: FileOnlyColumn[] = [
    {
      // "Location" — the workspace this file lives in — is a distinct
      // concept from "Related To" (a business-record link), which photos
      // don't have. This column used to be mislabeled "Related To" while
      // showing the workspace.
      title: t("globalFiles.columns.location", "Location"),
      key: "location",
      renderFile: (file: FileItem) =>
        file.projectInfo
          ? `${file.projectInfo.is_project ? t("globalFiles.relatedTo.project", "Project") : t("globalFiles.relatedTo.lead", "Lead")} · ${file.projectInfo.uid}`
          : file.isCompanyScoped
            ? t("globalFiles.location.companyFiles", "Company Files")
            : "—"
    },
    {
      title: t("globalFiles.columns.uploadedBy", "Uploaded By"),
      key: "uploadedBy",
      renderFile: (file: FileItem) => file.createdByName || "—"
    }
  ]

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center" wrap="wrap" gap="small">
        <Button
          icon={<DownloadOutlined />}
          disabled={!items.length}
          onClick={() =>
            downloadFilesAsZip(
              items.map((file) => ({ url: file.fileUrl, name: file.name })),
              t("globalFiles.photos.downloadAllZipName", "Photos & Media")
            )
          }
        >
          {t("globalFiles.photos.downloadAll", "Download All Photos & Media")}
        </Button>
        <Radio.Group
          value={view}
          onChange={(e) => setView(e.target.value)}
          optionType="button"
          options={[
            {
              label: (
                <span aria-label={t("files.view.grid")}>
                  <AppstoreOutlined />
                </span>
              ),
              value: "grid"
            },
            {
              label: (
                <span aria-label={t("files.view.list")}>
                  <BarsOutlined />
                </span>
              ),
              value: "list"
            }
          ]}
        />
      </Flex>

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : failed ? (
        <Empty description={t("globalFiles.allFiles.loadFailed", "Couldn't load files.")} />
      ) : !items.length ? (
        <Empty
          description={t(
            "globalFiles.photos.empty",
            "Photos uploaded to your accessible projects and leads will appear here."
          )}
        />
      ) : view === "grid" ? (
        <Row gutter={[12, 12]}>
          {items.map((file) => (
            <Col key={file.key} xs={12} sm={8} md={6} lg={4}>
              <FileCard
                file={file}
                onPreview={setPreviewFile}
                actions={actions.fileActions(file)}
                favorited={favorites.isFavorite(file.key)}
                onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <FilesListView
          items={items}
          actions={actions.fileActions}
          onPreview={setPreviewFile}
          isFavorite={(f) => favorites.isFavorite(f.key)}
          onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          extraColumns={extraColumns}
          columnConfigMode="global-photos"
        />
      )}

      {!loading && hasMore && (
        <Flex justify="center">
          <Button loading={loadingMore} onClick={loadMore}>
            {t("globalFiles.allFiles.loadMore", "Load more")}
          </Button>
        </Flex>
      )}

      {previewFile && (
        <FileViewer
          file={previewFile}
          items={items}
          onClose={() => setPreviewFile(null)}
          fileActions={actions.fileActions}
          isFavorite={(f) => favorites.isFavorite(f.key)}
          onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          onViewProject={navigateToProject}
          onRename={(f) => actions.setRenamingFile(f)}
          documentTypeOptions={actions.documentTypeOptions}
          documentTypesFailed={actions.documentTypesFailed}
          onChangeDocumentType={actions.updateDocumentType}
          statusOptions={actions.statusOptions}
          statusesFailed={actions.statusesFailed}
          onChangeStatus={actions.updateStatus}
          onShare={(f) => actions.setSharingFile(f)}
        />
      )}

      {actions.sharingFile && (
        <ShareDialog file={actions.sharingFile} onClose={() => actions.setSharingFile(null)} />
      )}

      {actions.renamingFile && (
        <RenameFileModal
          fileName={actions.renamingFile.name}
          onClose={() => actions.setRenamingFile(null)}
          onSubmit={(next) => actions.renameFile(actions.renamingFile as FileItem, next)}
        />
      )}

      {actions.assigningDocumentTypeFile && (
        <AssignDocumentTypeModal
          file={actions.assigningDocumentTypeFile}
          options={actions.documentTypeOptions}
          loadFailed={actions.documentTypesFailed}
          onClose={() => actions.setAssigningDocumentTypeFile(null)}
          onSubmit={(value) =>
            actions.updateDocumentType(actions.assigningDocumentTypeFile as FileItem, value)
          }
        />
      )}
    </Flex>
  )
}
